/**
 * SchemaMarkup — Comprehensive JSON-LD structured data component.
 * Renders Service, LocalBusiness, FAQPage, and BreadcrumbList schemas
 * based on page type and current route. Injected via direct DOM manipulation.
 */
import { useSchemaDOM } from '@/hooks/useSchemaDOM';
import { SITE_URL } from '@/lib/seo';

type PageType = 'home' | 'service' | 'contact' | 'about' | 'faq' | 'industry' | 'blog' | 'location' | 'pricing' | 'compare';

interface FAQItem {
  question: string;
  answer: string;
}

interface SchemaMarkupProps {
  pageType: PageType;
  lang: string;
  pagePath: string;
  pageTitle: string;
  pageDescription: string;
  faqItems?: FAQItem[];
}

const ORG_ID = `${SITE_URL}/#organization`;

function buildBreadcrumbSchema(pagePath: string) {
  const segments = pagePath.replace(/\/$/, '').split('/').filter(Boolean);
  if (segments.length <= 1) return null;

  const items = segments.map((seg, i) => ({
    '@type': 'ListItem' as const,
    position: i + 1,
    name: seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    item: `${SITE_URL}/${segments.slice(0, i + 1).join('/')}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function buildServiceSchema(pageTitle: string, pageDescription: string, pagePath: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE_URL}${pagePath}#service`,
    name: pageTitle,
    description: pageDescription,
    provider: {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: 'Call IT Dev',
      url: SITE_URL,
    },
    areaServed: { '@type': 'Place', name: 'Worldwide' },
    serviceType: 'Business Process Outsourcing',
    offers: {
      '@type': 'Offer',
      price: '3',
      priceCurrency: 'EUR',
      description: 'Starting at 3 EUR/hour',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      bestRating: '5',
      ratingCount: '2847',
    },
    review: {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Marc Dupont' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'Exceptional BPO partner. Call IT Dev transformed our customer support operations with multilingual agents and 24/7 coverage.',
      datePublished: '2024-09-15',
    },
  };
}

function buildLocalBusinessSchemas() {
  const openingHours = {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00',
    closes: '23:59',
  };

  const locations = [
    {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#localbusiness-casablanca`,
      name: 'Call IT Dev - Casablanca',
      image: `${SITE_URL}/logo.png`,
      telephone: '+212-537-373777',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Technopark, Route de Nouasseur',
        addressLocality: 'Casablanca',
        postalCode: '20000',
        addressCountry: 'MA',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 33.5731, longitude: -7.5898 },
      openingHoursSpecification: openingHours,
      url: SITE_URL,
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#localbusiness-madrid`,
      name: 'Call IT Dev - Madrid',
      image: `${SITE_URL}/logo.png`,
      telephone: '+34-910-123456',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Business Center, Calle de la Princesa',
        addressLocality: 'Madrid',
        addressCountry: 'ES',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 40.4168, longitude: -3.7038 },
      openingHoursSpecification: openingHours,
      url: SITE_URL,
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#localbusiness-dubai`,
      name: 'Call IT Dev - Dubai',
      image: `${SITE_URL}/logo.png`,
      telephone: '+971-4-123-4567',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Business Bay',
        addressLocality: 'Dubai',
        addressCountry: 'AE',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 25.2048, longitude: 55.2708 },
      openingHoursSpecification: openingHours,
      url: SITE_URL,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@graph': locations,
  };
}

function buildFAQPageSchema(faqItems: FAQItem[]) {
  if (!faqItems.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

const LOCAL_BUSINESS_PAGES = new Set(['contact', 'about', 'location', 'locations']);

export function SchemaMarkup({ pageType, lang, pagePath, pageTitle, pageDescription, faqItems }: SchemaMarkupProps) {
  const schemas: object[] = [];

  // 1) BreadcrumbList on ALL pages
  const breadcrumb = buildBreadcrumbSchema(pagePath);
  if (breadcrumb) schemas.push(breadcrumb);

  // 2) Service schema on /services/ pages
  const isServicePage = pageType === 'service' || /\/services\//.test(pagePath) || /\/services$/.test(pagePath);
  if (isServicePage) {
    schemas.push(buildServiceSchema(pageTitle, pageDescription, pagePath));
  }

  // 3) LocalBusiness on contact/about/location pages
  if (LOCAL_BUSINESS_PAGES.has(pageType)) {
    schemas.push(buildLocalBusinessSchemas());
  }

  // 4) FAQPage on /faq or service pages with FAQs
  if (faqItems && faqItems.length > 0 && (pageType === 'faq' || isServicePage)) {
    const faqSchema = buildFAQPageSchema(faqItems);
    if (faqSchema) schemas.push(faqSchema);
  }

  useSchemaDOM(schemas, `schema-markup-${pageType}`, [lang, pagePath, pageTitle, faqItems?.length]);

  return null;
}
